import { useRef, useState, useEffect } from "react";
import { cn } from "tailwind-variants";
import ChevronRightIcon from "assets/icons/arrow-right.svg?vue";
import { isMediumDeviceAndUp } from "common/utils/browser";
import debounce from "lodash.debounce";
import { VueTeleport } from "common/vue/VueTeleport";
import { useDtfStore } from "../store";
import ArtistEditsHeader from "./ArtistEditsHeader.vue";
import ArtistEditsInformation from "./ArtistEditsInformation.vue";
import { useMarkdownRenderer } from "./composables/useMarkdownRenderer";
import { trackTransfersPinningSessionEntryInput } from "./transfersPinningAnalytics";
import "./PinningSessionEntry.scss";

export default function PinningSessionEntry({ index, artwork, ...restProps }) {
  const { renderMarkdown } = useMarkdownRenderer();
  const store = useDtfStore();
  const isDesktop = isMediumDeviceAndUp();

  const [showInfoTooltip, setShowInfoTooltip] = useState(false);
  const [inputMessage, setInputMessage] = useState("");
  const [isHowItWorksOpen, setIsHowItWorksOpen] = useState(false);
  const [isDesktopPopoverVisible, setIsDesktopPopoverVisible] = useState(false);
  const [desktopPopoverStyle, setDesktopPopoverStyle] = useState({});

  const completedHeaderRef = useRef(null);
  const inputHeaderRef = useRef(null);
  const desktopPopoverElRef = useRef(null);
  const popoverHideTimeoutRef = useRef(null);
  const inputMessageRef = useRef("");
  const debouncedTrackEntryInputRef = useRef(null);

  function isLoading() {
    return store.pinningSessionLoading;
  }

  function isEnhancementInProgress() {
    if (!artwork?.id || artwork.isSimulated) return true;
    if (artwork.isLoading) return true;
    return store.isStandardArtwork && !artwork.enhancementCompleted;
  }

  useEffect(() => {
    inputMessageRef.current = inputMessage;
  }, [inputMessage]);

  useEffect(() => {
    debouncedTrackEntryInputRef.current = debounce(() => {
      if (artwork?.id) {
        trackTransfersPinningSessionEntryInput({
          artworkId: artwork.id,
          inputValue: inputMessageRef.current
        });
      }
    }, 500);
  }, [artwork?.id]);

  function getHowItWorksButton() {
    /* TODO(vue-migration): child component must expose howItWorksButtonRef via useImperativeHandle */
    return (
      completedHeaderRef.current?.howItWorksButtonRef ??
      inputHeaderRef.current?.howItWorksButtonRef ??
      null
    );
  }

  function showDesktopPopover() {
    clearTimeout(popoverHideTimeoutRef.current);
    const button = getHowItWorksButton();
    if (button) {
      const rect = button.getBoundingClientRect();
      const popoverWidth = 580;
      const gap = 8;
      const left = rect.left - popoverWidth - gap;
      setDesktopPopoverStyle({
        left: `${left}px`,
        top: "0px",
        visibility: "hidden"
      });
      setIsDesktopPopoverVisible(true);
      requestAnimationFrame(() => {
        const el = desktopPopoverElRef.current;
        const popoverHeight = el ? el.offsetHeight : 400;
        const buttonCenterY = rect.top + rect.height / 2;
        const idealTop = buttonCenterY - popoverHeight / 2;
        const margin = 8;
        const clampedTop = Math.max(
          margin,
          Math.min(idealTop, window.innerHeight - popoverHeight - margin)
        );
        setDesktopPopoverStyle({
          left: `${left}px`,
          top: `${clampedTop}px`
        });
      });
      return;
    }
    setIsDesktopPopoverVisible(true);
  }

  function hideDesktopPopover() {
    popoverHideTimeoutRef.current = setTimeout(() => {
      setIsDesktopPopoverVisible(false);
    }, 150);
  }

  function openHowItWorksModal() {
    if (!isDesktop) {
      setIsHowItWorksOpen(true);
    }
  }

  function closeHowItWorks() {
    setIsHowItWorksOpen(false);
  }

  async function handleSubmitMessage() {
    const trimmed = inputMessage.trim();
    if (!trimmed || isLoading() || isEnhancementInProgress()) return;
    await store.openPinningSession(index, trimmed);
    if (store.showPinningModal) {
      setInputMessage("");
    }
  }

  function handleKeyDown(e) {
    if (e.key === "Enter" && !e.shiftKey && !e.metaKey && !e.ctrlKey) {
      e.preventDefault();
      handleSubmitMessage();
    }
  }

  function handleEdit() {
    store.editPinningSession(index);
  }

  function handleRemove() {
    store.clearPinningSession(index);
  }

  return (
    <div
      {...restProps}
      className={cn("scope-95d9341c w-full", restProps?.className)}
      data-testid="pinning-session-entry"
    >
      {artwork.pinningSessionId ? (
        <div
          className="scope-95d9341c flex flex-col gap-3 rounded-lg bg-purple-200 p-2 md:p-3"
          data-testid="completed-session-indicator"
        >
          <ArtistEditsHeader
            className="scope-95d9341c"
            ref={completedHeaderRef}
            title="Get Expert Edits"
            onHowItWorksClick={openHowItWorksModal}
            onHowItWorksMouseenter={() => {
              isDesktop && showDesktopPopover();
            }}
            onHowItWorksMouseleave={() => {
              isDesktop && hideDesktopPopover();
            }}
            data-testid="artist-edits-header"
          />

          {artwork.pinningSummary ? (
            <div
              className="scope-95d9341c rounded-lg bg-white/70 p-3 text-sm text-gray-700"
              data-testid="markdown-content"
            >
              <div
                className="markdown-rendered scope-95d9341c text-inherit"
                dangerouslySetInnerHTML={{
                  __html: renderMarkdown(artwork.pinningSummary)
                }}
              />
            </div>
          ) : null}
          <div className="scope-95d9341c flex gap-2 self-end">
            <button
              className="scope-95d9341c shrink-0 cursor-pointer rounded-[8px] border border-red-600 bg-white px-3.5 py-2 text-sm font-semibold [font-family:inherit] hover:bg-red-50 disabled:pointer-events-none disabled:opacity-25"
              type="button"
              style={{ color: "#c90505" }}
              data-testid="cancel-design-request-button"
              onClick={handleRemove}
            >
              {" "}
              Cancel{" "}
            </button>
            <button
              className="scope-95d9341c shrink-0 cursor-pointer rounded-[8px] border-0 bg-blue-700 px-3.5 py-2 text-sm font-semibold !text-white [font-family:inherit] hover:bg-blue-800 disabled:pointer-events-none disabled:opacity-25"
              type="button"
              data-testid="edit-design-request-button"
              onClick={handleEdit}
            >
              {" "}
              Add more edits{" "}
              <ChevronRightIcon className="scope-95d9341c ml-1 inline size-4" data-testid="ml-1" />
            </button>
          </div>
        </div>
      ) : (
        <div
          className={cn(
            "scope-95d9341c",
            isEnhancementInProgress() && "cursor-not-allowed opacity-60"
          )}
        >
          <div className="scope-95d9341c rounded-t-lg bg-purple-200 px-2 pb-4 pt-[3px] md:px-3">
            <ArtistEditsHeader
              className="scope-95d9341c"
              ref={inputHeaderRef}
              title="Get Expert Edits"
              onHowItWorksClick={openHowItWorksModal}
              onHowItWorksMouseenter={() => {
                isDesktop && showDesktopPopover();
              }}
              onHowItWorksMouseleave={() => {
                isDesktop && hideDesktopPopover();
              }}
              data-testid="artist-edits-header-1"
            />
          </div>
          <div className="gradient-border-purple-blue scope-95d9341c relative z-10 -mt-3 flex flex-col gap-2 rounded-lg border-[1.5px] border-transparent px-2 py-1.5 md:px-3 md:py-2">
            <textarea
              className="mp-unmask border-0! outline-none! scope-95d9341c min-h-0 resize-none p-0 text-base placeholder:text-gray-400 disabled:cursor-not-allowed disabled:opacity-60 md:text-sm"
              value={inputMessage}
              onInput={e => {
                setInputMessage(e.target.value);
                debouncedTrackEntryInputRef.current?.();
              }}
              data-testid="request-designer-edit-input"
              placeholder="Tell us what to change and we'll get the details right. Our design team makes the edits after checkout."
              rows={2}
              disabled={isEnhancementInProgress()}
              onKeyDown={handleKeyDown}
            />

            <div className="scope-95d9341c flex justify-end">
              <button
                className={cn(
                  "scope-95d9341c",
                  "bg-gradient-purple-blue h-6 min-w-12 cursor-pointer rounded-lg border-0 px-2 !text-xs !font-semibold leading-4 !text-white [font-family:inherit] disabled:pointer-events-none disabled:opacity-25",
                  !inputMessage.trim() && !isLoading() && "opacity-50"
                )}
                type="button"
                data-testid="request-designer-edit-submit"
                disabled={isLoading() || isEnhancementInProgress()}
                onClick={handleSubmitMessage}
              >
                {isLoading() ? "Launching\u2026" : "Launch"}
              </button>
            </div>
          </div>
        </div>
      )}
      <VueTeleport to="body">
        {isDesktop && isDesktopPopoverVisible ? (
          <div
            className="scope-95d9341c fixed z-[10000] max-h-[90vh] w-[580px] overflow-y-auto rounded-lg border border-gray-200 bg-white p-4 text-sm shadow-lg"
            ref={desktopPopoverElRef}
            style={desktopPopoverStyle}
            onMouseEnter={() => {
              showDesktopPopover();
            }}
            onMouseLeave={() => {
              hideDesktopPopover();
            }}
            data-testid="fixed"
          >
            <ArtistEditsInformation
              className="scope-95d9341c"
              data-testid="artist-edits-information"
            />
          </div>
        ) : null}
      </VueTeleport>
      <VueTeleport to="body">
        {!isDesktop && isHowItWorksOpen ? (
          <div className="scope-95d9341c fixed inset-0 z-[10000] flex items-center justify-center px-4">
            <div
              className="scope-95d9341c absolute inset-0 bg-black/60"
              onClick={closeHowItWorks}
              data-testid="absolute"
            />

            <div className="scope-95d9341c relative max-h-[95dvh] w-full overflow-y-auto rounded-lg bg-white px-2.5 py-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
              <ArtistEditsInformation
                className="scope-95d9341c"
                isMobile={true}
                onClose={closeHowItWorks}
                data-testid="artist-edits-information-1"
              />
            </div>
          </div>
        ) : null}
      </VueTeleport>
    </div>
  );
}
